import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, Button, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  LinearProgress, Alert, IconButton, Tooltip, Table, TableHead,
  TableRow, TableCell, Tabs, Tab, Divider,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PaidIcon from '@mui/icons-material/Paid';
import {
  MY_BUDGET_CATEGORIES_QUERY, MY_BUDGET_SUMMARY_QUERY, MY_PAYMENT_MILESTONES_QUERY,
  MY_CEREMONIES_QUERY,
  CREATE_BUDGET_CATEGORY_MUTATION, UPDATE_BUDGET_CATEGORY_MUTATION, DELETE_BUDGET_CATEGORY_MUTATION,
  CREATE_BUDGET_ITEM_MUTATION, UPDATE_BUDGET_ITEM_MUTATION, DELETE_BUDGET_ITEM_MUTATION,
  CREATE_PAYMENT_MILESTONE_MUTATION, UPDATE_PAYMENT_MILESTONE_MUTATION,
  MARK_MILESTONE_AS_PAID_MUTATION, DELETE_PAYMENT_MILESTONE_MUTATION, SET_TOTAL_BUDGET_MUTATION,
} from '../graphql/queries';
import BudgetCategoryDialog from '../components/BudgetCategoryDialog';
import { BudgetStatusChip, BudgetBar, budgetSourceText } from '../components/BudgetStatus';

const ITEM_STATUS_COLOR = { pending:'default', confirmed:'info', paid:'success', overdue:'error', cancelled:'default' };
const MS_STATUS_COLOR   = { pending:'warning', paid:'success', overdue:'error', waived:'default' };
const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const EMPTY_ITEM = { title:'', vendorName:'', plannedAmount:'', actualAmount:'', paidAmount:'', status:'pending', dueDate:'', notes:'', ceremonyId:'' };
const EMPTY_MS   = { title:'', amount:'', dueDate:'', notes:'' };

export default function BudgetPage() {
  const [tab, setTab]     = useState(0);
  const [error, setError] = useState('');

  // Category dialog
  const [catDialog, setCatDialog] = useState(false);
  const [editCat, setEditCat]     = useState(null);

  // Item dialog
  const [itemDialog, setItemDialog] = useState(false);
  const [editItem, setEditItem]     = useState(null);
  const [itemForm, setItemForm]     = useState(EMPTY_ITEM);
  const [itemCatId, setItemCatId]   = useState('');

  // Milestone dialog
  const [msDialog, setMsDialog]   = useState(false);
  const [editMs, setEditMs]       = useState(null);
  const [msForm, setMsForm]       = useState(EMPTY_MS);
  const [msItemId, setMsItemId]   = useState('');

  const { data: catData, loading: catLoading, refetch: refetchCats } = useQuery(MY_BUDGET_CATEGORIES_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: summaryData, refetch: refetchSummary } = useQuery(MY_BUDGET_SUMMARY_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: msData, refetch: refetchMs } = useQuery(MY_PAYMENT_MILESTONES_QUERY, { fetchPolicy: 'cache-and-network', skip: tab !== 2 });
  const { data: ceremonyData } = useQuery(MY_CEREMONIES_QUERY, { fetchPolicy: 'cache-and-network' });

  const categories = catData?.myBudgetCategories || [];
  const summary    = summaryData?.myBudgetSummary || {};
  const milestones = msData?.myPaymentMilestones   || [];
  const ceremonies = ceremonyData?.myCeremonies?.filter(c => c.status !== 'cancelled') || [];

  // Every change is re-checked by the budget monitor on the server; refresh figures and status
  const refresh = () => { refetchCats(); refetchSummary(); };
  const onFail = e => setError(e.message);

  const [createCat] = useMutation(CREATE_BUDGET_CATEGORY_MUTATION, { onCompleted: () => { setCatDialog(false); refresh(); setError(''); }, onError: onFail });
  const [updateCat] = useMutation(UPDATE_BUDGET_CATEGORY_MUTATION, { onCompleted: () => { setCatDialog(false); setEditCat(null); refresh(); setError(''); }, onError: onFail });
  const [deleteCat] = useMutation(DELETE_BUDGET_CATEGORY_MUTATION, { onCompleted: refresh, onError: onFail });

  const [totalDialog, setTotalDialog] = useState({ open: false, value: '' });
  const [setTotal] = useMutation(SET_TOTAL_BUDGET_MUTATION, { onCompleted: () => { setTotalDialog({ open: false, value: '' }); refresh(); setError(''); }, onError: onFail });

  const [createItem] = useMutation(CREATE_BUDGET_ITEM_MUTATION, { onCompleted: () => { setItemDialog(false); setItemForm(EMPTY_ITEM); refresh(); setError(''); }, onError: onFail });
  const [updateItem] = useMutation(UPDATE_BUDGET_ITEM_MUTATION, { onCompleted: () => { setItemDialog(false); setEditItem(null); refresh(); setError(''); }, onError: onFail });
  const [deleteItem] = useMutation(DELETE_BUDGET_ITEM_MUTATION, { onCompleted: refresh, onError: onFail });

  const [createMs] = useMutation(CREATE_PAYMENT_MILESTONE_MUTATION, { onCompleted: () => { setMsDialog(false); setMsForm(EMPTY_MS); refresh(); refetchMs(); setError(''); }, onError: onFail });
  const [updateMs] = useMutation(UPDATE_PAYMENT_MILESTONE_MUTATION, { onCompleted: () => { setMsDialog(false); setEditMs(null); refetchMs(); setError(''); }, onError: onFail });
  const [markPaid] = useMutation(MARK_MILESTONE_AS_PAID_MUTATION,   { onCompleted: () => { refetchMs(); refresh(); } });
  const [deleteMs] = useMutation(DELETE_PAYMENT_MILESTONE_MUTATION, { onCompleted: () => { refetchMs(); refresh(); } });

  const openCreateCat = () => { setEditCat(null); setError(''); setCatDialog(true); };
  const openEditCat   = (c) => { setEditCat(c); setError(''); setCatDialog(true); };
  const handleSaveCat = (vars) => {
    if (editCat) updateCat({ variables: { id: editCat.id, ...vars } });
    else createCat({ variables: vars });
  };
  const watch = categories.filter(c => c.status !== 'ok');

  const openCreateItem = (catId) => { setEditItem(null); setItemForm(EMPTY_ITEM); setItemCatId(catId); setItemDialog(true); };
  const openEditItem   = (item) => { setEditItem(item); setItemForm({ title: item.title, vendorName: item.vendorName || '', plannedAmount: item.plannedAmount || '', actualAmount: item.actualAmount || '', paidAmount: item.paidAmount || '', status: item.status, dueDate: item.dueDate || '', notes: item.notes || '', ceremonyId: item.ceremonyId || '' }); setItemDialog(true); };
  const handleSaveItem = () => {
    const vars = { ...itemForm, plannedAmount: Number(itemForm.plannedAmount) || 0, actualAmount: Number(itemForm.actualAmount) || 0, paidAmount: Number(itemForm.paidAmount) || 0, ceremonyId: itemForm.ceremonyId || null };
    if (editItem) updateItem({ variables: { id: editItem.id, ...vars } });
    else createItem({ variables: { categoryId: itemCatId, ...vars } });
  };

  const openCreateMs = (itemId) => { setEditMs(null); setMsForm(EMPTY_MS); setMsItemId(itemId); setMsDialog(true); };
  const openEditMs   = (m) => { setEditMs(m); setMsForm({ title: m.title, amount: m.amount || '', dueDate: m.dueDate || '', notes: m.notes || '' }); setMsDialog(true); };
  const handleSaveMs = () => {
    const vars = { ...msForm, amount: Number(msForm.amount) || 0 };
    if (editMs) updateMs({ variables: { id: editMs.id, ...vars } });
    else createMs({ variables: { budgetItemId: msItemId, ...vars } });
  };

  const progressPct = summary.totalPlanned > 0 ? Math.min(100, (summary.totalPaid / summary.totalPlanned) * 100) : 0;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Budget &amp; Cost Management</Typography>
          <Typography variant="body2" color="text.secondary">
            A budget starts for every service you select. Spending is monitored: you are notified at 80% of a budget, when it is exceeded, and as payments fall due.
          </Typography>
        </Box>
        {tab === 0 && <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateCat}>Add Category</Button>}
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Budget Overview" />
        <Tab label="Expense Report" />
        <Tab label={`Payment Milestones${milestones.filter(m => m.status === 'pending').length > 0 ? ` (${milestones.filter(m => m.status === 'pending').length})` : ''}`} />
      </Tabs>

      {/* ── TAB 0: Overview ──────────────────────────────── */}
      {tab === 0 && (
        <Box>
          {/* Summary cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Total Budget',   value: fmt(summary.totalPlanned),  color: 'text.primary' },
              { label: 'Spent (committed)', value: fmt(summary.totalActual), color: summary.overBudget ? 'error.main' : 'text.primary' },
              { label: 'Total Paid',     value: fmt(summary.totalPaid),     color: 'success.main' },
              { label: summary.totalRemaining < 0 ? 'Over budget by' : 'Remaining', value: fmt(Math.abs(summary.totalRemaining || 0)), color: summary.totalRemaining < 0 ? 'error.main' : 'info.main' },
            ].map(s => (
              <Grid item xs={6} sm={3} key={s.label}>
                <Card><CardContent sx={{ textAlign:'center', py:'12px !important' }}>
                  <Typography variant="h6" fontWeight={700} color={s.color}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </CardContent></Card>
              </Grid>
            ))}
          </Grid>
          {/* Overall wedding budget (asked first in the Service Wizard) vs the service budgets */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent sx={{ py: '12px !important' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
                <Box>
                  <Typography fontWeight={600}>
                    Total wedding budget: {summary.totalBudget > 0 ? fmt(summary.totalBudget) : 'not set'}
                  </Typography>
                  <Typography variant="caption" color={summary.totalBudget > 0 && summary.allocatedBudget > summary.totalBudget ? 'error.main' : 'text.secondary'}>
                    {summary.totalBudget > 0
                      ? `${fmt(summary.allocatedBudget)} allocated to services · ${summary.allocatedBudget > summary.totalBudget
                        ? `${fmt(summary.allocatedBudget - summary.totalBudget)} over the total`
                        : `${fmt(summary.totalBudget - summary.allocatedBudget)} unallocated`}`
                      : 'Without one, the total budget is the sum of the service budgets.'}
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => setTotalDialog({ open: true, value: summary.totalBudget > 0 ? String(summary.totalBudget) : '' })}>
                  {summary.totalBudget > 0 ? 'Change' : 'Set'} Total Budget
                </Button>
              </Stack>
            </CardContent>
          </Card>
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" mb={0.5}>
              <Typography variant="caption">Payment Progress</Typography>
              <Typography variant="caption">{progressPct.toFixed(0)}% paid</Typography>
            </Stack>
            <LinearProgress variant="determinate" value={progressPct} color={summary.overBudget ? 'error' : 'success'} sx={{ height: 8, borderRadius: 4 }} />
          </Box>

          {/* Budget monitor */}
          {(watch.length > 0 || summary.overdueCount > 0) && (
            <Alert severity={watch.some(c => c.status === 'over') || summary.overdueCount > 0 ? 'error' : 'warning'} sx={{ mb: 2 }}>
              <strong>Budget monitor:</strong>{' '}
              {[
                ...watch.map(c => c.status === 'over'
                  ? `${c.name} is over budget (${fmt(c.totalActual)} of ${fmt(c.budgetAmount)})`
                  : `${c.name} has used ${c.usedPercent}% of its budget`),
                summary.overdueCount > 0 && `${summary.overdueCount} payment(s) overdue`,
              ].filter(Boolean).join(' · ')}
            </Alert>
          )}
          {summary.noBudgetCount > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {summary.noBudgetCount} categor{summary.noBudgetCount === 1 ? 'y has' : 'ies have'} no budget amount yet — set one (edit the category) so its spending can be monitored.
            </Alert>
          )}

          {catLoading && <LinearProgress sx={{ mb: 2 }} />}
          {categories.length === 0 && !catLoading && <Alert severity="info">No budget yet. It starts automatically for the services you select (Service Wizard) and the vendors you book — or click <strong>Add Category</strong>.</Alert>}

          {categories.map(cat => (
            <Card key={cat.id} sx={{ mb: 2, borderLeft: `4px solid ${cat.color || '#4a90e2'}` }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: cat.color || '#4a90e2' }} />
                    <Box>
                      <Typography fontWeight={600}>{cat.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{budgetSourceText(cat)}</Typography>
                    </Box>
                    <Chip label={`${(cat.items || []).length} items`} size="small" />
                    <BudgetStatusChip status={cat.status} />
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      Budget: {fmt(cat.budgetAmount)}{!cat.hasOwnBudget && ' (not set)'}  |  Paid: {fmt(cat.totalPaid)}
                    </Typography>
                    <Tooltip title="Add item"><IconButton size="small" onClick={() => openCreateItem(cat.id)}><AddIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Edit category"><IconButton size="small" onClick={() => openEditCat(cat)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete category"><IconButton size="small" color="error" onClick={() => deleteCat({ variables: { id: cat.id } })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </Stack>
                </Stack>
                <Box sx={{ mb: 1 }}>
                  <BudgetBar spent={cat.totalActual} budget={cat.budgetAmount} status={cat.status} usedPercent={cat.usedPercent} />
                </Box>
                {(cat.items || []).length > 0 && (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Item','Vendor','Planned','Actual','Paid','Status','Due','Actions'].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 600, py: 0.5 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cat.items.map(item => (
                        <TableRow key={item.id} hover>
                          <TableCell>
                            {item.title}
                            {item.fromBooking && <Chip label="Booking" size="small" variant="outlined" color="primary" sx={{ ml: 1, height: 18 }} />}
                          </TableCell>
                          <TableCell>{item.vendorName || '\u2014'}</TableCell>
                          <TableCell>{fmt(item.plannedAmount)}</TableCell>
                          <TableCell color={item.actualAmount > item.plannedAmount ? 'error' : undefined}>{fmt(item.actualAmount)}</TableCell>
                          <TableCell>{fmt(item.paidAmount)}</TableCell>
                          <TableCell><Chip label={item.status} size="small" color={ITEM_STATUS_COLOR[item.status] || 'default'} /></TableCell>
                          <TableCell>{item.dueDate ? new Date(item.dueDate).toLocaleDateString() : '\u2014'}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.25}>
                              <Tooltip title="Add milestone"><IconButton size="small" onClick={() => openCreateMs(item.id)}><PaidIcon fontSize="small" /></IconButton></Tooltip>
                              <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditItem(item)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                              {!item.fromBooking && (
                                <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => deleteItem({ variables: { id: item.id } })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* ── TAB 1: Expense Report ────────────────────────── */}
      {tab === 1 && (
        <Box>
          {categories.length === 0 && <Alert severity="info">No budget data to report yet.</Alert>}
          {categories.map(cat => (
            <Box key={cat.id} sx={{ mb: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: cat.color || '#4a90e2' }} />
                <Typography variant="h6" fontWeight={600}>{cat.name}</Typography>
                <Chip label={`Budget: ${fmt(cat.plannedAmount)}`} size="small" />
                <Chip label={`Actual: ${fmt(cat.totalActual)}`} size="small" color={cat.totalActual > cat.plannedAmount ? 'error' : 'success'} />
              </Stack>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    {['#','Item','Vendor/Ceremony','Planned','Actual','Paid','Balance','Status'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(cat.items || []).map((item, idx) => (
                    <TableRow key={item.id}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{item.title}</TableCell>
                      <TableCell>{item.vendorName || '\u2014'}</TableCell>
                      <TableCell>{fmt(item.plannedAmount)}</TableCell>
                      <TableCell sx={{ color: item.actualAmount > item.plannedAmount ? 'error.main' : 'inherit' }}>{fmt(item.actualAmount)}</TableCell>
                      <TableCell sx={{ color: 'success.main' }}>{fmt(item.paidAmount)}</TableCell>
                      <TableCell sx={{ color: item.paidAmount < item.actualAmount ? 'warning.main' : 'success.main' }}>
                        {fmt(item.actualAmount - item.paidAmount)}
                      </TableCell>
                      <TableCell><Chip label={item.status} size="small" color={ITEM_STATUS_COLOR[item.status] || 'default'} /></TableCell>
                    </TableRow>
                  ))}
                  <TableRow data-pinned sx={{ bgcolor: 'action.selected' }}>
                    <TableCell colSpan={3} sx={{ fontWeight: 700 }}>Category Total</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{fmt(cat.plannedAmount)}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: cat.totalActual > cat.plannedAmount ? 'error.main' : 'inherit' }}>{fmt(cat.totalActual)}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'success.main' }}>{fmt(cat.totalPaid)}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{fmt(cat.totalActual - cat.totalPaid)}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
              <Divider sx={{ mt: 1 }} />
            </Box>
          ))}
          {categories.length > 0 && (
            <Card sx={{ mt: 2, bgcolor: 'primary.main', color: 'white' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={1}>Grand Total</Typography>
                <Grid container spacing={2}>
                  {[['Total Budget', summary.totalPlanned], ['Total Actual', summary.totalActual], ['Total Paid', summary.totalPaid], ['Balance', summary.totalRemaining]].map(([label, val]) => (
                    <Grid item xs={6} sm={3} key={label}>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>{label}</Typography>
                      <Typography variant="h6" fontWeight={700}>{fmt(val)}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {/* ── TAB 2: Payment Milestones ─────────────────────── */}
      {tab === 2 && (
        <Box>
          {milestones.length === 0 && <Alert severity="info">No payment milestones yet. Add milestones from budget items.</Alert>}
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Item','Category','Milestone','Amount','Due','Paid','Status','Actions'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {milestones.map(m => (
                <TableRow key={m.id} hover>
                  <TableCell>{m.budgetItem?.title || '\u2014'}</TableCell>
                  <TableCell>{m.budgetItem?.category?.name || '\u2014'}</TableCell>
                  <TableCell>{m.title}</TableCell>
                  <TableCell>{fmt(m.amount)}</TableCell>
                  <TableCell sx={{ color: !m.paidDate && m.dueDate && new Date(m.dueDate) < new Date() ? 'error.main' : 'inherit' }}>
                    {m.dueDate ? new Date(m.dueDate).toLocaleDateString() : '\u2014'}
                  </TableCell>
                  <TableCell sx={{ color: 'success.main' }}>{m.paidDate ? new Date(m.paidDate).toLocaleDateString() : '\u2014'}</TableCell>
                  <TableCell><Chip label={m.status} size="small" color={MS_STATUS_COLOR[m.status] || 'default'} /></TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.25}>
                      {m.status !== 'paid' && (
                        <Tooltip title="Mark as paid"><IconButton size="small" color="success" onClick={() => markPaid({ variables: { id: m.id } })}><PaidIcon fontSize="small" /></IconButton></Tooltip>
                      )}
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditMs(m)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => deleteMs({ variables: { id: m.id } })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Total Budget Dialog */}
      <Dialog open={totalDialog.open} onClose={() => setTotalDialog({ open: false, value: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Total Wedding Budget</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            label="Total Budget (₹)" type="number" fullWidth autoFocus sx={{ mt: 1 }}
            value={totalDialog.value} inputProps={{ min: 0 }}
            onChange={e => setTotalDialog(d => ({ ...d, value: e.target.value }))}
            helperText="Leave empty or 0 to use the sum of the service budgets"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTotalDialog({ open: false, value: '' })}>Cancel</Button>
          <Button variant="contained" onClick={() => setTotal({ variables: { amount: Number(totalDialog.value) || 0 } })}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Category Dialog */}
      <BudgetCategoryDialog
        open={catDialog} editing={editCat} error={error}
        existingServices={categories.map(c => c.serviceCategory).filter(Boolean)}
        onClose={() => { setCatDialog(false); setError(''); }} onSave={handleSaveCat}
      />

      {/* Item Dialog */}
      <Dialog open={itemDialog} onClose={() => setItemDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editItem ? 'Edit Budget Item' : 'New Budget Item'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Item Title" value={itemForm.title} onChange={e => setItemForm(f => ({ ...f, title: e.target.value }))} fullWidth required />
            <Stack direction="row" spacing={2}>
              <TextField label="Vendor / Provider" value={itemForm.vendorName} onChange={e => setItemForm(f => ({ ...f, vendorName: e.target.value }))} fullWidth />
              <TextField select label="Ceremony" value={itemForm.ceremonyId} onChange={e => setItemForm(f => ({ ...f, ceremonyId: e.target.value }))} fullWidth>
                <MenuItem value="">None</MenuItem>
                {ceremonies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={4}><TextField label="Planned (₹)" type="number" value={itemForm.plannedAmount} onChange={e => setItemForm(f => ({ ...f, plannedAmount: e.target.value }))} fullWidth /></Grid>
              <Grid item xs={4}><TextField label="Actual (₹)"  type="number" value={itemForm.actualAmount}  onChange={e => setItemForm(f => ({ ...f, actualAmount: e.target.value }))}  fullWidth /></Grid>
              <Grid item xs={4}><TextField label="Paid (₹)"    type="number" value={itemForm.paidAmount}    onChange={e => setItemForm(f => ({ ...f, paidAmount: e.target.value }))}    fullWidth /></Grid>
            </Grid>
            <Stack direction="row" spacing={2}>
              <TextField select label="Status" value={itemForm.status} onChange={e => setItemForm(f => ({ ...f, status: e.target.value }))} fullWidth>
                {['pending','confirmed','paid','overdue','cancelled'].map(s => <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>)}
              </TextField>
              <TextField label="Due Date" type="date" value={itemForm.dueDate} onChange={e => setItemForm(f => ({ ...f, dueDate: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
            </Stack>
            <TextField label="Notes" value={itemForm.notes} onChange={e => setItemForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={2} />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setItemDialog(false); setError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveItem}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Milestone Dialog */}
      <Dialog open={msDialog} onClose={() => setMsDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editMs ? 'Edit Milestone' : 'New Payment Milestone'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Milestone Title" value={msForm.title} onChange={e => setMsForm(f => ({ ...f, title: e.target.value }))} fullWidth required placeholder="e.g. Advance, Final Payment" />
            <Stack direction="row" spacing={2}>
              <TextField label="Amount (₹)" type="number" value={msForm.amount} onChange={e => setMsForm(f => ({ ...f, amount: e.target.value }))} fullWidth />
              <TextField label="Due Date" type="date" value={msForm.dueDate} onChange={e => setMsForm(f => ({ ...f, dueDate: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
            </Stack>
            <TextField label="Notes" value={msForm.notes} onChange={e => setMsForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={2} />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setMsDialog(false); setError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveMs}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
